import { Clear as ClearIcon, Search as SearchIcon } from "@mui/icons-material";
import {
  Badge,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";

export interface FilterOption {
  label: string;
  value: string;
  tooltip?: string;
}

interface FilterSelectSearchableProps {
  label: string;
  startIcon: React.ReactNode;
  options: FilterOption[];
  selectedOptions: string[];
  setSelectedOptions: (selectedOptions: string[]) => void;
  hideTextWhenSmall?: boolean;
  renderOptionSuffix?: (option: FilterOption) => React.ReactNode;
  pinnedOptionValues?: string[];
}

const FilterSelectSearchable = ({
  label,
  startIcon,
  options,
  selectedOptions,
  setSelectedOptions,
  hideTextWhenSmall = false,
  renderOptionSuffix,
  pinnedOptionValues = [],
}: FilterSelectSearchableProps) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const filters = new Set(selectedOptions);
  const badgeCount = selectedOptions.length;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();

  const isLgOrBelow = useMediaQuery(theme.breakpoints.down("lg"));

  const handleClickMenuItem = (v: string) => {
    const newFilters = new Set(filters);
    if (filters.has(v)) {
      newFilters.delete(v);
    } else {
      newFilters.add(v);
    }
    setSelectedOptions([...newFilters.keys()]);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
    setSearchText("");
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpen(false);
    setSearchText("");
  };

  const pinnedSet = new Set(pinnedOptionValues);
  const pinnedOptions = options.filter((o) => pinnedSet.has(o.value));
  const unpinnedOptions = options.filter((o) => !pinnedSet.has(o.value));
  const orderedOptions = [...pinnedOptions, ...unpinnedOptions];

  const filteredOptions = orderedOptions.filter((o) =>
    o.label.toLowerCase().includes(searchText.toLowerCase()),
  );

  return (
    <>
      {isLgOrBelow && hideTextWhenSmall ? (
        <IconButton
          onClick={handleOpenMenu}
          data-testid={`filter-select-${label}`}
        >
          <Badge badgeContent={badgeCount} color="secondary">
            {startIcon}
          </Badge>
        </IconButton>
      ) : (
        <Button
          onClick={handleOpenMenu}
          startIcon={startIcon}
          sx={{
            paddingRight: badgeCount ? 2 : 0,
          }}
          data-testid={`filter-select-${label}`}
        >
          <Stack
            direction="row"
            sx={{
              gap: 2,
              alignItems: "center",
            }}
          >
            {label}
            <Badge badgeContent={badgeCount} color="secondary" />
          </Stack>
        </Button>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        sx={{ "& .MuiList-root": { paddingY: 0 } }}
      >
        <Stack
          sx={{
            overflow: "auto",
            maxHeight: 400,
            maxWidth: 295,
          }}
        >
          <MenuItem
            disableRipple
            onClick={(e) => e.stopPropagation()}
            sx={{ px: 2, py: 1, paddingTop: 2 }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: searchText ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchText("")}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </MenuItem>

          {filteredOptions.length === 0 ? (
            <MenuItem
              disabled
              sx={{
                "&.Mui-disabled": {
                  color: "grey.400",
                  opacity: 1,
                  justifyContent: "center",
                  paddingTop: 2,
                  paddingBottom: 1.5,
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.primary",
                }}
              >
                No results found
              </Typography>
            </MenuItem>
          ) : (
            filteredOptions.map((option: FilterOption) => (
              <MenuItem
                key={option?.value}
                onClick={() => handleClickMenuItem(option?.value)}
                sx={{ px: 2, py: 1 }}
              >
                <Checkbox checked={filters.has(option?.value)} />
                <Tooltip title={option?.tooltip} arrow>
                  <ListItemText
                    primary={option?.label}
                    slotProps={{
                      primary: {
                        sx: {
                          whiteSpace: "normal",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                        },
                      },
                    }}
                    sx={{
                      "& .MuiListItemText-primary": {
                        whiteSpace: "normal",
                      },
                    }}
                  />
                </Tooltip>
                {renderOptionSuffix && renderOptionSuffix(option)}
              </MenuItem>
            ))
          )}

          <MenuItem
            disableRipple
            onClick={() => {
              setSelectedOptions([]);
            }}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              padding: 0,
            }}
          >
            <Button
              variant="text"
              size="small"
              fullWidth
              disabled={selectedOptions.length === 0}
              sx={{
                paddingY: 2,
                paddingLeft: 2.5,
                justifyContent: "flex-start",
              }}
              startIcon={<ClearIcon />}
            >
              Clear all
            </Button>
          </MenuItem>
        </Stack>
      </Menu>
    </>
  );
};

export default FilterSelectSearchable;
